import * as types from '../mutation-types';
import FacebookPagesAPI from '../../api/facebookPages';

const createTabState = () => ({
  comments: [],
  pagination: {
    currentPage: 1,
    totalCount: 0,
    totalPages: 0,
    perPage: 10,
  },
  filters: {},
  searchWord: '',
  scrollPosition: 0,
});

const updateCommentsState = ($state, data, stateKey) => {
  if (!$state[stateKey]) return;

  $state[stateKey].pagination = {
    currentPage: data.current_page,
    totalCount: data.total_count,
    totalPages: data.total_pages,
    perPage: data.per_page,
  };

  if (data.current_page === 1) {
    $state[stateKey].comments = data.comments;
    return;
  }

  if (!Array.isArray(data.comments)) return;

  const REPLACE_STATES_FOR_PAGINATION = ['responded', 'pending'];

  if (REPLACE_STATES_FOR_PAGINATION.includes(stateKey)) {
    $state[stateKey].comments = data.comments;
    return;
  }

  // Append para postComments, mentioned, testModeHistory
  const existingIds = new Set(
    $state[stateKey].comments.map(comment => comment.id)
  );
  const filteredComments = data.comments.filter(
    comment => !existingIds.has(comment.id)
  );

  $state[stateKey].comments = [
    ...$state[stateKey].comments,
    ...filteredComments,
  ];
};

export const state = {
  postComments: {
    comments: [],
    pagination: {
      currentPage: 1,
      totalCount: 0,
      totalPages: 0,
      perPage: 10,
    },
  },
  pending: createTabState(),
  responded: createTabState(),
  mentioned: createTabState(),
  tabCounts: {
    pending: 0,
    responded: 0,
    mentioned: 0,
  },
  tabLoaded: {
    pending: false,
    responded: false,
    mentioned: false,
  },
  postInfo: {},
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    testMode: {
      isFetchingTestMode: false,
      hasError: false,
    },
  },
  testModeHistory: {
    comments: [],
    pagination: {
      currentPage: 1,
      totalCount: 0,
      totalPages: 0,
      perPage: 10,
    },
  },
  customClassifications: [],
};

export const getters = {
  getPostComments($state) {
    return $state.postComments;
  },
  getPendingComments($state) {
    return $state.pending;
  },
  getRespondedComments($state) {
    return $state.responded;
  },
  getMentionedComments($state) {
    return $state.mentioned;
  },
  getTabCounts($state) {
    return $state.tabCounts;
  },
  getTabState: $state => tab => {
    return $state[tab] || createTabState();
  },
  getPostInfo($state) {
    return $state.postInfo;
  },
  getUIFlags($state) {
    return $state.uiFlags;
  },
  getTestModeInfo($state) {
    return $state.testMode;
  },
  getTestModeHistory($state) {
    return $state.testModeHistory;
  },
  getCustomClassifications($state) {
    return $state.customClassifications;
  },
};

export const actions = {
  getPostComments: async (
    { commit },
    { pageId, postId, filters, commentText, pagination }
  ) => {
    try {
      const response = await FacebookPagesAPI.getPostComments(
        pageId,
        postId,
        filters,
        commentText,
        pagination
      );
      commit(types.default.SET_POST_COMMENTS, response.data);
    } catch (error) {
      commit(types.default.SET_POST_COMMENTS, {
        comments: [],
        pagination: {
          currentPage: 1,
          totalCount: 0,
          totalPages: 0,
          perPage: 10,
        },
      });
    }
  },
  getTabComments: async (
    { commit },
    { tab, filters, commentText, pagination }
  ) => {
    commit(types.default.SET_POST_COMMENTS_FETCHING_STATUS, true);
    try {
      const response = await FacebookPagesAPI.getComments(
        filters,
        commentText,
        pagination,
        tab
      );

      commit(types.default.SET_TAB_COMMENTS, { tab, data: response.data });

      if (response.data.counts) {
        commit(types.default.SET_TAB_COUNTS, response.data.counts);
      }
    } catch (error) {
      commit(types.default.SET_TAB_COMMENTS, {
        tab,
        data: {
          comments: [],
          current_page: 1,
          total_count: 0,
          total_pages: 0,
          per_page: 10,
        },
      });
    } finally {
      commit(types.default.SET_POST_COMMENTS_FETCHING_STATUS, false);
    }
  },

  getComments: async ({ dispatch }, { filters, commentText, pagination }) => {
    await Promise.all([
      dispatch('getTabComments', {
        tab: 'pending',
        filters,
        commentText,
        pagination,
      }),
      dispatch('getTabComments', {
        tab: 'responded',
        filters,
        commentText,
        pagination,
      }),
      dispatch('getTabComments', {
        tab: 'mentioned',
        filters,
        commentText,
        pagination,
      }),
    ]);
  },
  saveTabState: ({ commit }, { tab, filters, searchWord, pagination }) => {
    commit(types.default.SET_TAB_STATE, {
      tab,
      filters,
      searchWord,
      pagination,
    });
  },
  saveTabScrollPosition: ({ commit }, { tab, scrollPosition }) => {
    commit(types.default.SET_TAB_SCROLL_POSITION, { tab, scrollPosition });
  },
  getPostInfo: async ({ commit }, { pageId, postId }) => {
    commit(types.default.SET_POST_COMMENTS_FETCHING_STATUS, true);
    try {
      const response = await FacebookPagesAPI.getPost(pageId, postId);
      commit(types.default.SET_POST_COMMENTS_FETCHING_STATUS, false);
      commit(types.default.SET_POST_INFO, response.data.post);
    } catch (error) {
      commit(types.default.SET_POST_COMMENTS_FETCHING_STATUS, false);
      if (error.response?.status === 410 && error.response?.data?.deleted) {
        const err = new Error(error.response?.data?.error || 'Post deleted');
        err.deletedPost = {
          postId: error.response.data.post_id ?? postId,
          pageId,
        };
        throw err;
      }
      throw error;
    }
  },
  createReply: async (
    { commit, dispatch, state: vuexState },
    { pageId, postId, commentId, message, callback }
  ) => {
    commit(types.default.SET_POST_COMMENTS_FETCHING_STATUS, true);
    try {
      const response = await FacebookPagesAPI.createReply(
        pageId,
        postId,
        commentId,
        message
      );

      commit(types.default.ADD_POST_COMMENT_REPLY, response.data.reply);

      const respondedState = vuexState.responded;
      const hasServerRespondedCount =
        vuexState.tabCounts && vuexState.tabCounts.responded > 0;

      if (
        (!vuexState.tabLoaded?.responded ||
          !(
            Array.isArray(respondedState?.comments) &&
            respondedState.comments.length > 0
          )) &&
        hasServerRespondedCount
      ) {
        const pagination = respondedState?.pagination || {
          currentPage: 1,
          perPage: 10,
        };
        const filters = respondedState?.filters || {};
        const commentText = respondedState?.searchWord || '';

        await dispatch('getTabComments', {
          tab: 'responded',
          filters,
          commentText,
          pagination,
        });
      }

      commit(types.default.SET_POST_COMMENTS_FETCHING_STATUS, false);
      if (callback) callback({ success: true });
    } catch (error) {
      commit(types.default.SET_POST_COMMENTS_FETCHING_STATUS, false);
      if (error.response?.status === 410 && error.response?.data?.deleted) {
        const deletedCommentId = error.response.data.comment_id || commentId;
        if (callback) {
          callback({
            success: false,
            deleted: true,
            commentId: deletedCommentId,
            error: error.response.data.error,
          });
          // Don't throw error if callback handles it
        }
      } else {
        if (callback) {
          callback({
            success: false,
            deleted: false,
            error: error.response?.data || error.message,
          });
          // Don't throw error if callback handles it
          return;
        }
        throw error;
      }
    }
  },

  hide: async ({ commit }, { pageId, postId, commentId, callback }) => {
    try {
      await FacebookPagesAPI.hideComment(pageId, postId, commentId);
      commit(types.default.HIDE_POST_COMMENT, commentId);
      if (callback) callback({ success: true });
    } catch (error) {
      if (error.response?.status === 410 && error.response?.data?.deleted) {
        const deletedCommentId = error.response.data.comment_id || commentId;
        if (callback) {
          callback({
            success: true,
            deleted: true,
            commentId: deletedCommentId,
            error: error.response.data.error,
          });
        }
      } else if (callback) callback({ success: false });
    }
  },

  verifyCommentExists: async (_, { pageId, postId, commentId }) => {
    try {
      await FacebookPagesAPI.verifyCommentExists(pageId, postId, commentId);
      return { exists: true };
    } catch (error) {
      if (error.response?.status === 410 && error.response?.data?.deleted) {
        throw error; // Re-throw to be handled by caller
      }
      throw error;
    }
  },

  delete: async ({ commit }, { pageId, postId, commentId, callback }) => {
    try {
      await FacebookPagesAPI.deleteComment(pageId, postId, commentId);
      commit(types.default.DELETE_POST_COMMENT, commentId);
      if (callback) callback({ success: true });
    } catch (error) {
      // If comment was already deleted on Instagram, controller will delete from DB
      // and return 200, so we should still remove from frontend
      if (error.response?.status === 410 && error.response?.data?.deleted) {
        const deletedCommentId = error.response.data.comment_id || commentId;
        // Comment was deleted from DB by controller, remove from frontend
        commit(types.default.DELETE_POST_COMMENT, deletedCommentId);
        if (callback) {
          callback({
            success: true,
            deleted: true,
            commentId: deletedCommentId,
            error: error.response.data.error,
          });
        }
      } else if (callback) callback({ success: false });
    }
  },

  getCustomClassifications: async ({ commit }) => {
    try {
      const response = await FacebookPagesAPI.getCustomClassifications();
      commit(
        types.default.SET_CUSTOM_CLASSIFICATIONS,
        response.data.custom_classifications
      );
    } catch (error) {
      commit(types.default.SET_CUSTOM_CLASSIFICATIONS, []);
    }
  },

  testMode: async ({ commit }, { pageId, postId, message }) => {
    try {
      commit('IS_FETCHING_TEST_MODE', { status: true, hasError: false });
      const response = await FacebookPagesAPI.testMode(pageId, postId, message);
      commit('SET_TEST_MODE', response.data);
      commit('IS_FETCHING_TEST_MODE', { status: false, hasError: false });
    } catch (error) {
      commit('SET_TEST_MODE', { ai_comment: null, person_comment: null });
      commit('IS_FETCHING_TEST_MODE', { status: false, hasError: true });
    }
  },

  clearTestMode: ({ commit }) => {
    commit('SET_TEST_MODE', { ai_comment: null, person_comment: null });
    commit('IS_FETCHING_TEST_MODE', { status: false, hasError: false });
  },

  fetchTestModeHistory: async (
    { commit },
    { pageId, postId, isFake, pagination }
  ) => {
    try {
      const response = await FacebookPagesAPI.fetchTestModeHistory(
        pageId,
        postId,
        isFake,
        pagination
      );
      commit('SET_TEST_MODE_HISTORY', response.data);
    } catch (error) {
      commit('SET_TEST_MODE_HISTORY', {
        comments: [],
        pagination: {
          currentPage: 1,
          totalCount: 0,
          totalPages: 0,
          perPage: 10,
        },
      });
    }
  },

  sendDirectMessage: async (_, { commentId, message, callback }) => {
    try {
      const response = await FacebookPagesAPI.sendDirectMessage(
        commentId,
        message
      );
      if (callback) callback({ success: true, data: response.data });
      return response.data;
    } catch (error) {
      if (callback) {
        callback({
          success: false,
          error: error.response?.data || error.message,
        });
        return;
      }
      throw error;
    }
  },
};

export const mutations = {
  [types.default.SET_CUSTOM_CLASSIFICATIONS]($state, classifications) {
    $state.customClassifications = classifications;
  },

  [types.default.SET_POST_COMMENTS_FETCHING_STATUS]($state, status) {
    $state.uiFlags.isFetching = status;
  },

  ['IS_FETCHING_TEST_MODE']($state, { status, hasError }) {
    $state.uiFlags.testMode.isFetchingTestMode = status;
    $state.uiFlags.testMode.hasError = hasError;
  },

  [types.default.SET_TAB_COMMENTS]($state, { tab, data }) {
    if (!$state[tab]) return;

    $state[tab].pagination = {
      currentPage: data.current_page,
      totalCount: data.total_count,
      totalPages: data.total_pages,
      perPage: data.per_page,
    };

    $state[tab].comments = data.comments || [];

    if (!$state.tabLoaded) {
      $state.tabLoaded = { pending: false, responded: false, mentioned: false };
    }
    $state.tabLoaded[tab] = !!(data.comments && data.comments.length > 0);
  },

  [types.default.SET_TAB_COUNTS]($state, counts) {
    $state.tabCounts = {
      pending: counts.pending || 0,
      responded: counts.responded || 0,
      mentioned: counts.mentioned || 0,
    };
  },

  [types.default.SET_TAB_STATE](
    $state,
    { tab, filters, searchWord, pagination }
  ) {
    if (!$state[tab]) return;
    if (filters !== undefined) $state[tab].filters = filters;
    if (searchWord !== undefined) $state[tab].searchWord = searchWord;
    if (pagination !== undefined) {
      $state[tab].pagination = { ...$state[tab].pagination, ...pagination };
    }
  },

  [types.default.SET_TAB_SCROLL_POSITION]($state, { tab, scrollPosition }) {
    if (!$state[tab]) return;
    $state[tab].scrollPosition = scrollPosition;
  },

  [types.default.SET_PENDING_COMMENTS]($state, data) {
    updateCommentsState($state, data, 'pending');
  },

  [types.default.SET_RESPONDED_COMMENTS]($state, data) {
    updateCommentsState($state, data, 'responded');
  },

  [types.default.SET_MENTIONED_COMMENTS]($state, data) {
    updateCommentsState($state, data, 'mentioned');
  },

  [types.default.SET_POST_COMMENTS]($state, data) {
    updateCommentsState($state, data, 'postComments');
  },

  [types.default.SET_POST_INFO]: ($state, postInfo) => {
    $state.postInfo = postInfo;
  },

  [types.default.ADD_POST_COMMENT_REPLY]: ($state, data) => {
    if (!$state.postComments?.comments) return;

    const incomingId = data.comment_id;
    const replyData = { ...data };
    delete replyData.comment_id;

    const collections = [
      $state.postComments,
      $state.pending,
      $state.responded,
    ].filter(c => c?.comments);

    let parentCommentId = incomingId;

    const directMatch = collections.find(col =>
      col.comments.some(comment => comment.id === incomingId)
    );

    if (!directMatch) {
      const parentMatch = collections
        .map(col =>
          col.comments.find(
            comment =>
              Array.isArray(comment.replies) &&
              comment.replies.some(r => r.id === incomingId)
          )
        )
        .find(Boolean);

      if (parentMatch) {
        parentCommentId = parentMatch.id;
      }
    }

    $state.postComments.comments = $state.postComments.comments.map(comment => {
      if (comment.id === parentCommentId) {
        return { ...comment, replies: [...(comment.replies || []), replyData] };
      }
      return comment;
    });

    const pending = $state.pending;
    if (pending?.comments) {
      const pendingComment = pending.comments.find(
        comment => comment.id === parentCommentId
      );

      if (pendingComment) {
        const updated = {
          ...pendingComment,
          replies: [...(pendingComment.replies || []), replyData],
        };

        if (incomingId === parentCommentId) {
          pending.comments = pending.comments.filter(
            comment => comment.id !== parentCommentId
          );

          if (pending.pagination?.totalCount >= 1) {
            pending.pagination.totalCount -= 1;
          }

          if ($state.mentioned?.comments) {
            $state.mentioned.comments = $state.mentioned.comments.filter(
              comment => comment.id !== parentCommentId
            );

            if ($state.mentioned.pagination?.totalCount >= 1) {
              $state.mentioned.pagination.totalCount -= 1;
            }
          }

          if (!$state.responded) {
            $state.responded = {
              comments: [],
              pagination: {
                currentPage: 1,
                totalCount: 0,
                totalPages: 0,
                perPage: 10,
              },
            };
          }

          $state.responded.comments = [updated, ...$state.responded.comments];

          if ($state.responded.pagination) {
            $state.responded.pagination.totalCount =
              ($state.responded.pagination.totalCount || 0) + 1;
          }

          return;
        }

        pending.comments = pending.comments.map(comment =>
          comment.id === parentCommentId ? updated : comment
        );

        if ($state.responded?.comments) {
          $state.responded.comments = $state.responded.comments.map(comment =>
            comment.id === parentCommentId ? updated : comment
          );
        }

        return;
      }
    }

    if ($state.responded?.comments) {
      $state.responded.comments = $state.responded.comments.map(comment => {
        if (comment.id === parentCommentId) {
          return {
            ...comment,
            replies: [...(comment.replies || []), replyData],
          };
        }
        return comment;
      });
    }
  },

  [types.default.DELETE_POST_COMMENT]: ($state, commentId) => {
    const isParentInPost = !!($state.postComments?.comments || []).find(
      comment => comment && comment.id === commentId
    );
    const isParentInTabs = ['pending', 'responded', 'mentioned'].some(
      tab =>
        !!($state[tab]?.comments || []).find(
          comment => comment && comment.id === commentId
        )
    );
    const isParent = !!(isParentInPost || isParentInTabs);

    if (isParent) {
      if ($state.postComments.comments) {
        $state.postComments.comments = $state.postComments.comments.filter(
          comment => comment.id !== commentId
        );
      }

      ['pending', 'responded', 'mentioned'].forEach(tab => {
        if ($state[tab].comments) {
          const existed = $state[tab].comments.some(
            comment => comment.id === commentId
          );
          $state[tab].comments = $state[tab].comments.filter(
            comment => comment.id !== commentId
          );
          if (existed && $state.tabCounts[tab] > 0) {
            $state.tabCounts[tab] -= 1;
          }
        }
      });

      if ($state.postComments.comments) {
        $state.postComments.comments = $state.postComments.comments.map(
          comment => ({
            ...comment,
            replies:
              comment.replies?.filter(reply => reply.id !== commentId) || [],
          })
        );
      }
    } else {
      if ($state.postComments.comments) {
        $state.postComments.comments = $state.postComments.comments.map(
          comment => ({
            ...comment,
            replies:
              comment.replies?.filter(reply => reply.id !== commentId) || [],
          })
        );
      }

      ['pending', 'responded', 'mentioned'].forEach(tab => {
        if ($state[tab].comments) {
          $state[tab].comments = $state[tab].comments.map(comment => ({
            ...comment,
            replies:
              comment.replies?.filter(reply => reply.id !== commentId) || [],
          }));
        }
      });
    }
  },

  [types.default.HIDE_POST_COMMENT]: ($state, commentId) => {
    const postComments = $state.postComments?.comments || [];

    const hasParentInPostComments = postComments.some(
      comment => comment && comment.id === commentId
    );

    const hasParentInTabs = ['pending', 'responded', 'mentioned'].some(tab =>
      ($state[tab]?.comments || []).some(
        comment => comment && comment.id === commentId
      )
    );

    const isParentComment = hasParentInPostComments || hasParentInTabs;

    if (isParentComment) {
      if ($state.postComments.comments) {
        $state.postComments.comments = $state.postComments.comments.map(
          comment =>
            comment.id === commentId
              ? { ...comment, hidden: !comment.hidden }
              : comment
        );
      }

      ['pending', 'responded', 'mentioned'].forEach(tab => {
        if ($state[tab].comments) {
          $state[tab].comments = $state[tab].comments.map(comment =>
            comment.id === commentId
              ? { ...comment, hidden: !comment.hidden }
              : comment
          );
        }
      });
    } else {
      if ($state.postComments.comments) {
        $state.postComments.comments = $state.postComments.comments.map(
          comment => ({
            ...comment,
            replies:
              comment.replies?.map(reply =>
                reply.id === commentId
                  ? { ...reply, hidden: !reply.hidden }
                  : reply
              ) || [],
          })
        );
      }

      ['pending', 'responded', 'mentioned'].forEach(tab => {
        if ($state[tab].comments) {
          $state[tab].comments = $state[tab].comments.map(comment => ({
            ...comment,
            replies:
              comment.replies?.map(reply =>
                reply.id === commentId
                  ? { ...reply, hidden: !reply.hidden }
                  : reply
              ) || [],
          }));
        }
      });
    }
  },

  [types.default.CLEAR_POST_COMMENTS]: $state => {
    $state.postComments = [];
  },

  ['SET_TEST_MODE']: ($state, data) => {
    const { ai_comment, person_comment } = data;
    $state.testMode = { ai_comment, person_comment };
    if (!person_comment) {
      updateCommentsState(
        $state,
        {
          comments: [],
          pagination: {
            currentPage: 1,
            totalCount: 0,
            totalPages: 0,
            perPage: 10,
          },
        },
        'testModeHistory'
      );
    }
  },

  ['SET_TEST_MODE_HISTORY']: ($state, data) => {
    updateCommentsState($state, data, 'testModeHistory');
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
